'use client';

import { c as _c } from "react/compiler-runtime";
import type { ProductOption } from '@repo/shopify';
import { useCallback, useMemo } from 'react';
import { IntentPrefetchLink } from '@/components/ui/intent-prefetch-link';
import { buildVariantSearchParams, getColorOption, getColorSwatchStyle, type ProductVariantSelectionLike, resolveSelectedVariant } from './product-option-utils';
interface LinkedColorProduct {
  handle: string;
  title: string;
  options: ProductOption[];
  variants: ProductVariantSelectionLike[];
  colorValues: string[];
  colorOptionName: string | null;
}
interface LinkedProductColorSelectorProps {
  currentHandle: string;
  options: ProductOption[];
  variants: ProductVariantSelectionLike[];
  linkedProducts: LinkedColorProduct[];
  selectedOptions: Record<string, string>;
  onColorChange: (optionName: string, value: string) => void;
}
interface SwatchOption {
  handle: string;
  title: string;
  value: string;
  options: ProductOption[];
  variants: ProductVariantSelectionLike[];
  colorOptionName: string | null;
  isCurrentProduct: boolean;
}
export function LinkedProductColorSelector(t0) {
  const $ = _c(33);
  const {
    currentHandle,
    options,
    variants,
    linkedProducts,
    selectedOptions,
    onColorChange
  } = t0;
  let t1;
  if ($[0] !== options) {
    t1 = getColorOption(options);
    $[0] = options;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const currentColorOption = t1;
  const currentColorName = currentColorOption?.name;
  const currentColorValue = currentColorName ? selectedOptions[currentColorName] : "";
  currentColorOption?.values;
  let nextSwatches;
  if ($[2] !== currentColorName || $[3] !== currentColorOption?.values || $[4] !== currentHandle || $[5] !== linkedProducts || $[6] !== options || $[7] !== variants) {
    nextSwatches = new Map();
    for (const value of currentColorOption?.values || []) {
      nextSwatches.set(value.trim().toLowerCase(), {
        handle: currentHandle,
        title: "Current product",
        value,
        options,
        variants,
        colorOptionName: currentColorName || null,
        isCurrentProduct: true
      });
    }
    for (const product of linkedProducts) {
      for (const value_0 of product.colorValues) {
        const key = value_0.trim().toLowerCase();
        if (nextSwatches.has(key)) {
          continue;
        }
        nextSwatches.set(key, {
          handle: product.handle,
          title: product.title,
          value: value_0,
          options: product.options,
          variants: product.variants,
          colorOptionName: product.colorOptionName,
          isCurrentProduct: false
        });
      }
    }
    $[2] = currentColorName;
    $[3] = currentColorOption?.values;
    $[4] = currentHandle;
    $[5] = linkedProducts;
    $[6] = options;
    $[7] = variants;
    $[8] = nextSwatches;
  } else {
    nextSwatches = $[8];
  }
  const swatches = nextSwatches;
  let t2;
  if ($[9] !== selectedOptions) {
    t2 = swatch => {
      const destinationSelection = swatch.options.reduce((acc, option) => {
        if (option.name === swatch.colorOptionName) {
          acc[option.name] = swatch.value;
          return acc;
        }
        const selectedValue = selectedOptions[option.name];
        if (selectedValue && option.values.includes(selectedValue)) {
          acc[option.name] = selectedValue;
        }
        return acc;
      }, {});
      const params = buildVariantSearchParams(resolveSelectedVariant(swatch.variants, destinationSelection));
      const query = params.toString();
      return query ? `/products/${swatch.handle}?${query}` : `/products/${swatch.handle}`;
    };
    $[9] = selectedOptions;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const getLinkedHref = t2;
  if (swatches.size <= 1) {
    return null;
  }
  let t3;
  if ($[11] !== variants) {
    t3 = (optionName, optionValue) => variants.some(variant => {
      if (!variant.availableForSale) {
        return false;
      }
      return variant.selectedOptions.every(option_0 => {
        if (option_0.name === optionName) {
          return option_0.value === optionValue;
        }
        return true;
      });
    });
    $[11] = variants;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const isCurrentColorAvailable = t3;
  let t4;
  if ($[13] !== currentColorValue) {
    t4 = currentColorValue && <span className="ml-2 font-normal normal-case tracking-normal text-neutral-900">{currentColorValue}</span>;
    $[13] = currentColorValue;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== t4) {
    t5 = <div className="mb-2"><span className="text-sm font-bold uppercase tracking-wide text-neutral-900">Color:{t4}</span></div>;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== getLinkedHref || $[18] !== isCurrentColorAvailable || $[19] !== onColorChange || $[20] !== selectedOptions || $[21] !== swatches) {
    let t7;
    if ($[23] !== getLinkedHref || $[24] !== isCurrentColorAvailable || $[25] !== onColorChange || $[26] !== selectedOptions) {
      t7 = swatch_0 => {
        const colorOptionName = swatch_0.colorOptionName;
        const isSelected = swatch_0.isCurrentProduct && !!colorOptionName && selectedOptions[colorOptionName] === swatch_0.value;
        if (swatch_0.isCurrentProduct && colorOptionName) {
          const isAvailable = isCurrentColorAvailable(colorOptionName, swatch_0.value);
          return <button key={`${swatch_0.handle}-${swatch_0.value}`} type="button" className={`group relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full p-0 transition-opacity ${isAvailable ? "" : "opacity-40"}`} onClick={() => onColorChange(colorOptionName, swatch_0.value)} title={swatch_0.value} disabled={!isAvailable}><span className={`block h-8 w-8 shrink-0 rounded-full border border-neutral-300 ${isSelected ? "ring-2 ring-neutral-900 ring-offset-2" : isAvailable ? "group-hover:ring-2 group-hover:ring-neutral-300 group-hover:ring-offset-2" : ""}`} style={getColorSwatchStyle(swatch_0.value, swatch_0.options)} />{!isAvailable && <span className="absolute inset-0 flex items-center justify-center"><span className="block h-10 w-px rotate-45 bg-neutral-400" /></span>}</button>;
        }
        const linkedHref = getLinkedHref(swatch_0);
        return <IntentPrefetchLink key={`${swatch_0.handle}-${swatch_0.value}`} href={linkedHref} replace={true} scroll={false} className="group relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full p-0" title={`View ${swatch_0.value} on ${swatch_0.title}`}><span className="block h-8 w-8 shrink-0 rounded-full border border-neutral-300 group-hover:ring-2 group-hover:ring-neutral-300 group-hover:ring-offset-2" style={getColorSwatchStyle(swatch_0.value, swatch_0.options)} /></IntentPrefetchLink>;
      };
      $[23] = getLinkedHref;
      $[24] = isCurrentColorAvailable;
      $[25] = onColorChange;
      $[26] = selectedOptions;
      $[27] = t7;
    } else {
      t7 = $[27];
    }
    t6 = Array.from(swatches.values()).sort(_temp).map(t7);
    $[17] = getLinkedHref;
    $[18] = isCurrentColorAvailable;
    $[19] = onColorChange;
    $[20] = selectedOptions;
    $[21] = swatches;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  let t7;
  if ($[28] !== t6) {
    t7 = <div className="flex flex-wrap gap-2">{t6}</div>;
    $[28] = t6;
    $[29] = t7;
  } else {
    t7 = $[29];
  }
  let t8;
  if ($[30] !== t5 || $[31] !== t7) {
    t8 = <div>{t5}{t7}</div>;
    $[30] = t5;
    $[31] = t7;
    $[32] = t8;
  } else {
    t8 = $[32];
  }
  return t8;
}
function _temp(a, b) {
  return a.value.localeCompare(b.value);
}